import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { categories, type Category } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);

  useEffect(() => {
    const found = categories.find((c) => c.slug === slug) || categories[0];
    setActiveCategory(found);
  }, [slug]);

  const filteredArticles = activeCategory
    ? articles.filter((a) => a.category.slug === activeCategory.slug)
    : [];

  if (!activeCategory) {
    return (
      <>
        <SEOHead title="分类页面" description="91网资源分类页面" />
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <p className="text-foreground-500">分类未找到</p>
        </div>
        <Footer />
      </>
    );
  }

  const categoryJsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${activeCategory.name} - 91网`,
    "description": activeCategory.description,
    "url": `https://sddiweite.cn/category/${activeCategory.slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${activeCategory.name} - 91网`}
        description={activeCategory.description}
        keywords={`91网,${activeCategory.name},资源分类,91网资源`}
        canonicalUrl={`https://sddiweite.cn/category/${activeCategory.slug}`}
        jsonLd={categoryJsonLd}
      />
      <Navbar />
      <main className="pt-24 pb-20">
        <div className="mx-auto px-6" style={{ maxWidth: 1280 }}>
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: activeCategory.name },
            ]}
          />

          <div className="mt-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-500">
              {activeCategory.description}
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory.slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`} />
                {cat.name}
                <span className={`ml-1.5 text-xs ${activeCategory.slug === cat.slug ? 'text-background-50/70' : 'text-foreground-400'}`}>
                  {cat.count}
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.length > 0 ? (
              filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-foreground-400">{article.publishDate}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs text-secondary-600 bg-secondary-100 px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <div className="col-span-full text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl" />
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}